<template>
  <div class="page has-compressed-header">
    <n-loading-spinner v-if="!templateLoaded" />

    <section v-if="templateLoaded" class="section primary-header">
      <nav class="level container is-fluid">
        <div class="level-left">
          <h4 class="title is-4">
            <EditableText :value="templateName" :placeholder="'Enter template name...'" @text-updated="onNameChange" />
          </h4>
        </div>

        <div class="level-right">
          <p class="level-item">
            <n-button id="save-content-button" variant="secondary" :loading="updateInProgress" @click="saveTemplate">
              <span>{{ templateId ? 'Save' : 'Create' }}</span>
            </n-button>
          </p>

          <p class="level-item">
            <n-button id="cancel-save-content-button" variant="secondary--outline" @click="cancel">
              <span>Cancel</span>
            </n-button>
          </p>
        </div>
      </nav>
    </section>

    <section v-if="templateLoaded" class="section content-container">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column is-6">
            <div class="title is-5">HTML</div>
            <div :id="EDITOR_ID"></div>
          </div>
          <div class="column is-6">
            <div class="title is-5">Preview</div>
            <div class="preview-container">
              <iframe title="preview" :srcdoc="previewHtml" sandbox=""></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ConfirmationModal
      v-if="showDiscardChangesModel"
      confirmationTitle="Discard changes"
      confirmationMessage="Are you sure you wish to discard your changes"
      v-on:confirm="goBackToEmailSettings"
      v-on:cancel="showDiscardChangesModel = false"
    />
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Toast from '@/shared/services/toast';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { emailApi } from '../services/emailApiService';
import * as ace from 'brace';
import 'brace/mode/html';
import 'brace/theme/monokai';
import 'brace/ext/language_tools';
import EditableText from '@/shared/components/EditableText.vue';
import { sanitizeHtml } from '@/shared/util/sanitize-html/sanitizeHtml';
import { Issues } from '@/shared/util/sanitize-html/types';
import { emailTokenPlaceholders } from '../services/emailTokenPlaceholders';
import { htmlFormatter } from '../services/htmlFormatter';
import { SANITIZE_OPTIONS } from '@/shared/util/sanitize-html/EmailTemplateSanitizeOptions';
import { useStore } from 'vuex';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const TOKENS = {
  messages: '{{ messages }}',
  senderName: '{{ senderName }}',
};
const EDITOR_ID = 'email-template-editor';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const route = useRoute();
const router = useRouter();

const templateId = ref('');
const templateName = ref('');
const templateHtml = ref('');
const previewHtml = ref('');
const templateHtmlHasIssues = ref(false);
const templateChanged = ref(false);
const templateLoaded = ref(false);
const updateInProgress = ref(false);
const showDiscardChangesModel = ref(false);

let editor: ace.Editor;
let editorAnnotationTimeout: number;

onMounted(async () => {
  templateId.value = route.params.templateId as string;
  templateName.value = route.query.templateName as string;
  await loadTemplate();
  setupAceEditor();
});

onBeforeUnmount(() => {
  templateHtml.value = '';
  previewHtml.value = '';
  templateChanged.value = false;
});

async function loadTemplate() {
  if (templateId.value) {
    try {
      const htmlContent: string = await emailApi.getTemplateContent(currentWorkspace.value.id, templateId.value);
      templateHtml.value = htmlContent;
      setAceEditorValue(htmlContent);
      setPreviewValue(htmlContent);
      templateLoaded.value = true;
    } catch (error: any) {
      if (error.status !== 404) {
        handleSeriousError(error.message);
      }
    }
  } else {
    templateHtml.value = `<!DOCTYPE html>\r\n<html><head></head><body>${TOKENS.messages}</body></html>`;
    setAceEditorValue(TOKENS.messages);
    setPreviewValue(templateHtml.value);
    templateLoaded.value = true;
  }
}

function setupAceEditor() {
  if (isEditorReadyForSetup()) {
    const langTools = ace.acequire('ace/ext/language_tools');
    langTools.setCompleters([]);

    editor = ace.edit(EDITOR_ID);
    editor.getSession().setMode('ace/mode/html');
    editor.setTheme('ace/theme/monokai');
    editor.setShowPrintMargin(false);
    editor.session.on('change', (event: ace.EditorChangeEvent) => setTemplateHtmlOnChange());

    editor.setOptions({
      enableBasicAutocompletion: true,
    });

    const customCompleter = {
      getCompletions: (editor: ace.Editor, session: ace.IEditSession, pos: number, prefix: any, callback: any) => {
        const suggestions = [
          { caption: 'Content Placeholder', value: TOKENS.messages, meta: 'Metadata' },
          { caption: "Sender's Name Placeholder", value: TOKENS.senderName, meta: 'Metadata' },
        ];

        callback(null, suggestions);
      },
    };

    langTools.addCompleter(customCompleter);

    if (templateHtml.value) {
      setAceEditorValue(templateHtml.value);
    }

    editor.resize();
  }
}

function isEditorReadyForSetup(): boolean {
  return !editor && !!document.getElementById(EDITOR_ID);
}

function setTemplateHtmlOnChange() {
  const newContent: string = editor.session.getValue();

  if (templateHtml.value !== newContent) {
    updateTemplateHtml(newContent);
  }
}

function updateTemplateHtml(newContent: string) {
  templateHtml.value = newContent;

  setPreviewValue(getSafeHtml(newContent));
  templateChanged.value = true;
}

function setPreviewValue(html: string) {
  const whiteBackgroundStyle = '<style>body {background-color: white;}</style>';
  previewHtml.value = whiteBackgroundStyle + replaceTokens(html);
}

function setAceEditorValue(newValue: string) {
  if (editor) {
    editor.session.setValue(newValue);
  }
}

function replaceTokens(newContent: string): string {
  return newContent
    .replace(TOKENS.messages, emailTokenPlaceholders.getContentPlaceholder())
    .replace(TOKENS.senderName, emailTokenPlaceholders.getSenderNamePlaceholder());
}

function getSafeHtml(unCleaned: string): string {
  // https://www.npmjs.com/package/sanitize-html

  const response = sanitizeHtml(unCleaned, SANITIZE_OPTIONS);
  showEditorWarningsAfterDelay(response.issues);
  return response.output;
}

function showEditorWarningsAfterDelay(issues: Issues[]) {
  templateHtmlHasIssues.value = issues.length > 0;
  if (editorAnnotationTimeout) {
    clearTimeout(editorAnnotationTimeout);
  }
  editorAnnotationTimeout = window.setTimeout(() => showEditorWarnings(issues), 1000);
}

function showEditorWarnings(issues: Issues[]) {
  editor.getSession().setAnnotations(
    issues.map((issue) => {
      return {
        row: issue.line - 1,
        column: issue.column,
        text: issue.description,
        type: 'error', // also warning and information
      };
    }),
  );
}

function onNameChange(newName: string) {
  templateChanged.value = true;
  templateName.value = newName;
}

async function saveTemplate() {
  if (validateSaveRequest()) {
    try {
      updateInProgress.value = true;
      templateHtml.value = htmlFormatter.format(templateHtml.value);
      await callSaveApi();
      templateChanged.value = false;
      Toast.showSuccess('Template saved');
      goBackToEmailSettings();
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      updateInProgress.value = false;
    }
  }
}

function callSaveApi(): Promise<void> {
  if (templateId.value) {
    return emailApi.saveEmailTemplate(
      currentWorkspace.value.id,
      templateId.value,
      templateName.value,
      templateHtml.value,
    );
  } else {
    return emailApi.createEmailTemplate(currentWorkspace.value.id, templateName.value, templateHtml.value);
  }
}

function validateSaveRequest(): boolean {
  if (!templateName.value) {
    Toast.showError('Template name cannot be empty');
    return false;
  }
  if (templateHtml.value.indexOf(TOKENS.messages) === -1) {
    Toast.showError(`Placeholder ${TOKENS.messages} must be present in template`);
    return false;
  }
  if (templateHtmlHasIssues.value) {
    Toast.showError(`HTML content contains issues, please resolve before saving`);
    return false;
  }
  return true;
}

function cancel() {
  if (templateChanged.value) {
    showDiscardChangesModel.value = true;
  } else {
    goBackToEmailSettings();
  }
}

function goBackToEmailSettings() {
  router.push({ name: 'email' });
}
</script>

<style scoped lang="scss">
.section {
  padding: 0;
}

.content-container {
  display: flex;
  height: 100%;
  width: 100%;
}

#email-template-editor {
  position: absolute;
  top: 80px;
  bottom: 75px;
  left: 0;
  right: 5px;
}

.columns {
  height: 100%;
}

.column {
  padding-top: 30px;
  position: relative;
}

.preview-container {
  position: absolute;
  top: 80px;
  bottom: 75px;
  left: 5px;
  right: 0;
}

iframe {
  width: 100%;
  height: 100%;
}
</style>
